import { useState, useMemo } from 'react';
import DTSelectedItemTransactionView from '../../DTSelectedItemTransactionView';
import { useSelector, useDispatch } from 'react-redux';
import { getDecisionNodeAtPath, extractSelectedNonHistoricalTicketFromState } from '../../../../../../extractFromState';
import { Title, SubTitle } from '../../../../DecisionTree.styles';
import {
  FormContainer,
  Container,
  Row,
  Label,
  Input,
  ResultWrapper,
  ResultValue,
  InputWrapper,
} from './DTorderSubtotalCalculator.styles';
import { handleError } from 'services/ErrorService/ErrorService';
import { updatePartnerTags } from 'api/partnerTags';
import { addEdgeToDecisionTreePath } from '../../../../../../actions/searchActions';
import { setLoadingUnique, addNoteToSelectedTicket } from 'actions';

const DTOrderSubtotalCalculator = () => {
  const dispatch = useDispatch();
  const setLoading = (val) => dispatch(setLoadingUnique(val, 'itemTransactionContainer'));
  const decisionNode = useSelector(getDecisionNodeAtPath);
  const ticket = useSelector(extractSelectedNonHistoricalTicketFromState);
  const currentField = useMemo(() => {
    const ticketFields = ticket.moreTicketInfo?.ticketFields || [];
    const field = ticketFields.find((curField) => curField.title === decisionNode.fieldName);

    return field || {};
  }, [decisionNode, ticket]);

  const [total, setTotal] = useState('');
  const [taxes, setTaxes] = useState('');
  const [shipping, setShipping] = useState('');

  const calculatedSubtotal = +(+total - +taxes - +shipping).toFixed(2);
  const threshold = +decisionNode.increaseThreshold || 0;
  const calculatedThreshold = threshold + +currentField.value;

  const handleInputChange = (value, handler) => {
    const regExp = /^[0-9]+\.?([0-9]{1,2}$)*$/;
    if (value !== '' && !regExp.test(value)) {
      return;
    }

    handler(value);
  };

  const nextButtonHandler = async () => {
    if (!currentField.id) {
      return handleError({
        error: 'Error processing your request.',
        showPopup: true,
        customFields: { currentField, ticket },
      });
    }
    try {
      setLoading(true);
      const currentValue = +currentField.value;

      if (threshold && calculatedSubtotal > calculatedThreshold) {
        dispatch(
          addNoteToSelectedTicket(
            `Ticket field "${decisionNode.fieldName}" was not updated because the calculated amount was greater than the set threshold.`
          )
        );
        dispatch(addEdgeToDecisionTreePath('exceeded_threshold'));
      } else if (calculatedSubtotal < calculatedThreshold && calculatedSubtotal > 0) {
        await updatePartnerTags(ticket, [{ id: currentField.id, value: calculatedSubtotal }]);
        dispatch(
          addNoteToSelectedTicket(
            `Ticket field "${decisionNode.fieldName}" has been updated from ${currentValue} to ${calculatedSubtotal} by Simplr Agent`
          )
        );
        dispatch(addEdgeToDecisionTreePath('success'));
      } else {
        dispatch(
          addNoteToSelectedTicket(
            `Ticket field "${decisionNode.fieldName}" was not updated because the original value of ${currentValue} was correct`
          )
        );
        dispatch(addEdgeToDecisionTreePath('success'));
      }
    } catch (error) {
      handleError({ error, showPopup: true, customFields: { currentField, ticket } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DTSelectedItemTransactionView
      showContainer
      nextButtonLabel="NEXT"
      nextButtonClickHandler={nextButtonHandler}
      isNextEnabled={total && taxes && shipping}
    >
      <Container>
        {decisionNode.title && <Title>{decisionNode.title}</Title>}
        {decisionNode.subText && <SubTitle>{decisionNode.subText}</SubTitle>}
        <FormContainer>
          <Row>
            <Label htmlFor={'orderTotal'}>Order total:</Label>
            <InputWrapper>
              <Input
                id={'orderTotal'}
                value={total}
                onChange={({ target: { value } }) => handleInputChange(value, setTotal)}
                placeholder={'0'}
              />
            </InputWrapper>
          </Row>
          <Row>
            <Label htmlFor={'taxes'}>Taxes:</Label>
            <InputWrapper>
              <Input
                id={'taxes'}
                value={taxes}
                onChange={({ target: { value } }) => handleInputChange(value, setTaxes)}
                placeholder={'0'}
              />
            </InputWrapper>
          </Row>
          <Row>
            <Label htmlFor={'shipping'}>Shipping:</Label>
            <InputWrapper>
              <Input
                id={'shipping'}
                value={shipping}
                onChange={({ target: { value } }) => handleInputChange(value, setShipping)}
                placeholder={'0'}
              />
            </InputWrapper>
          </Row>
          <ResultWrapper>
            <Label>Subtotal:</Label>
            <ResultValue>${calculatedSubtotal.toFixed(2)}</ResultValue>
          </ResultWrapper>
        </FormContainer>
      </Container>
    </DTSelectedItemTransactionView>
  );
};

export default DTOrderSubtotalCalculator;
